import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

private val ATTENDANCE_LABELS = listOf("registered" to "Přihlášen", "present" to "Prezentován", "absent" to "Neúčast", "cancelled" to "Odhlášen")
private fun TourEntryDto.occupiesLane() = attendance == "registered" || attendance == "present"
private data class TourRegistrationData(
    val tournament: TourTournamentDto,
    val slots: List<TourSlotDto>, val entries: List<TourEntryDto>,
    val set: TourHandicapSetDto?, val handicaps: List<TourHandicapDto>,
    val draws: List<TourDrawDto>, val placements: List<TourDrawEntryDto>,
    val players: List<TourPlayerDto>, val startedPlayers: Set<String>, val jokerUses: List<TourJokerUseDto>,
)

private suspend fun loadTourRegistration(token: String?, id: String, registrationMode: Boolean = false): TourRegistrationData = coroutineScope {
    val tournament = TourOperationsApi.tournament(token, id)
    val slotsTask = async { TourOperationsApi.slots(token, id) }
    val entriesTask = async { TourOperationsApi.entries(token, id) }
    val setsTask = async { TourOperationsApi.sets(token, tournament.seasonId) }
    val playersTask = async { if (token != null) TourApi.players(token) else emptyList() }
    val startedTask = async { if (token != null && registrationMode) TourOperationsApi.startedPlayers(token, tournament.seasonId) else emptySet() }
    val jokersTask = async { if (token != null && !registrationMode) TourOperationsApi.jokerUses(token, tournament.seasonId) else emptyList() }
    val slots = slotsTask.await()
    val set = effectiveTourSet(setsTask.await(), tournament)
    val handicapsTask = async {
        if (set == null) emptyList() else if (token != null) TourOperationsApi.handicaps(token, set.id)
        else emptyList()
    }
    val drawTasks = slots.map { slot -> async {
        TourOperationsApi.draws(token, slot.id).firstOrNull { it.id == slot.currentDrawId && (token != null || it.rosterVersion == slot.rosterVersion) }
    } }
    val draws = drawTasks.mapNotNull { it.await() }
    val placementTasks = draws.map { draw -> async { TourOperationsApi.drawEntries(token, draw.id) } }
    TourRegistrationData(tournament, slots, entriesTask.await(), set, handicapsTask.await(), draws,
        placementTasks.flatMap { it.await() }, playersTask.await(), startedTask.await(), jokersTask.await())
}

@Composable
internal fun TourRegistrationEditor(token: String, tournamentId: String, registrationMode: Boolean = false, onChanged: () -> Unit = {}) {
    var data by remember { mutableStateOf<TourRegistrationData?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var refresh by remember { mutableStateOf(0) }
    var savingPlayerId by remember { mutableStateOf<String?>(null) }
    var savedPlayerId by remember { mutableStateOf<String?>(null) }
    var saveAttempt by remember { mutableStateOf(0) }
    var move by remember { mutableStateOf<Pair<TourEntryDto, String>?>(null) }
    var start by remember { mutableStateOf<TourSlotDto?>(null) }
    var redraw by remember { mutableStateOf<TourSlotDto?>(null) }
    var manualDraw by remember { mutableStateOf<TourSlotDto?>(null) }
    val scope = rememberCoroutineScope()
    LaunchedEffect(refresh) {
        loading = true; error = null
        try { data = loadTourRegistration(token, tournamentId, registrationMode) }
        catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    fun operate(action: suspend () -> Unit) {
        if (busy || loading) return
        scope.launch {
            busy = true; error = null
            try { action(); loading = true; refresh++; onChanged() }
            catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { busy = false }
        }
    }
    fun assignPlayer(player: TourPlayerDto, slotId: String) {
        if (busy || loading) return
        val current = data ?: return
        scope.launch {
            busy = true; savingPlayerId = player.id; savedPlayerId = null; error = null
            try {
                val entry = current.entries.firstOrNull { it.playerId == player.id && it.attendance != "cancelled" }
                if (slotId.isBlank()) {
                    if (entry != null) TourOperationsApi.entry(token, current.tournament, entry, "cancelled", false)
                } else TourOperationsApi.register(token, current.tournament, player.id, slotId)
                data = loadTourRegistration(token, tournamentId, registrationMode)
                savedPlayerId = player.id; onChanged()
            } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { saveAttempt++; savingPlayerId = null; busy = false }
        }
    }
    TourAdminStatus(loading, busy, if (start == null && redraw == null && manualDraw == null) error else null, "Načítám obsazení…", "Načíst aktuální obsazení") {
        move = null; start = null; redraw = null; manualDraw = null; refresh++
    }
    val current = data ?: return
    val t = current.tournament
    val closed = t.status == "finished" || t.playPhase != "qualification"
    val disabled = busy || loading || closed
    val registrationClosed = if (registrationMode) !tourRegistrationOpen(t) else !t.presentationStarted && t.status !in listOf("running", "finished")
    if (registrationMode && registrationClosed) Div(attrs = { classes("card") }) {
        P { Text("Přihlášky nejsou otevřené.") }
        if (t.status == "published") Button(attrs = { classes("primary"); if (disabled) disabled(); onClick { operate { TourApi.saveTournament(token, t, t.startsOn, "open") } } }) { Text("Otevřít přihlášky") }
    }
    if (t.playPhase != "qualification") Div(attrs = { classes("card") }) {
        P { Text("Kvalifikace je uzavřená. Pokračujte v kroku Semifinále nebo Finále.") }
    }
    if (registrationMode && current.slots.isEmpty()) Div(attrs = { classes("card") }) {
        P { Text("Vytvořte dvě rundy v 16:00 a 17:20, každou pro nejvýše 24 hráčů na osmi drahách.") }
        Button(attrs = { classes("primary"); if (disabled) disabled(); onClick { operate { TourOperationsApi.prepareSlots(token, t) } } }) { Text("Vytvořit dvě rundy") }
    }
    if (current.slots.isNotEmpty()) {
        if (registrationMode && !closed) Div(attrs = { classes("card") }) {
            H2 { Text("Přihlásit hráče") }
            TourRegistrationList(current.players, current.entries, current.slots, current.startedPlayers,
                disabled || registrationClosed, saveAttempt, savingPlayerId, savedPlayerId, ::assignPlayer)
            TourAdminDisclosure("Evidence hráčů a nový host") { TourPlayersAdmin(token) { refresh++ } }
            P(attrs = { classes("muted") }) { Text("Změna přihlášky se ukládá ihned. Nový host je po uložení dostupný v seznamu všech hráčů.") }
        }
        val orderedSlots = if (registrationMode) current.slots else current.slots.sortedWith(
            compareBy<TourSlotDto> { it.startedAt != null }.thenBy { it.roundNo })
        orderedSlots.forEach { slot -> key(slot.id) {
            val entries = current.entries.filter { it.slotId == slot.id && it.attendance != "cancelled" }
            val active = entries.filter { it.occupiesLane() }
            val waiting = current.entries.filter { it.waitlistSlotId == slot.id && it.attendance != "cancelled" }.sortedBy { it.waitlistOrder }
            val isStarted = slot.startedAt != null
            val validDraw = current.draws.firstOrNull { it.id == slot.currentDrawId && it.rosterVersion == slot.rosterVersion }
            var time by remember(slot.startsAt) { mutableStateOf(slot.startsAt.take(5)) }
            var capacity by remember(slot.capacity) { mutableStateOf(slot.capacity.toString()) }
            Div(attrs = { classes("card") }) {
                H2 { Text("${slot.roundNo}. runda · ${slot.startsAt.take(5)} · ${active.size}/${slot.capacity}") }
                if (registrationMode) {
                    H4 { Text("Přihlášení hráči") }
                    if (active.isEmpty()) P(attrs = { classes("muted") }) { Text("V této rundě zatím nejsou přihlášení hráči.") }
                    else Ul(attrs = { classes("tour-round-players"); attr("aria-label", "Přihlášení hráči ${slot.roundNo}. rundy") }) {
                        val names = current.players.associate { it.id to (it.fullName?.takeIf { name -> name.isNotBlank() } ?: it.name) }
                        active.sortedWith(compareBy(tourNameComparator) { names[it.playerId] ?: it.playerName }).forEach { e ->
                            Li { Text(names[e.playerId] ?: e.playerName) }
                        }
                    }
                }
                if (isStarted) P { Text("Runda byla zahájena. Prezentace, Joker, handicapy a los jsou uzamčené.") }
                if (registrationMode) TourAdminDisclosure("Nastavení ${slot.roundNo}. rundy") {
                Div(attrs = { classes("tour-round-settings") }) {
                    TourField("Čas ${slot.roundNo}. rundy", time, disabled || isStarted, InputType.Time) { time = it }
                    TourField("Kapacita ${slot.roundNo}. rundy", capacity, disabled || isStarted, InputType.Text) { capacity = it }
                    Button(attrs = {
                        if (disabled || isStarted || capacity.toIntOrNull() !in 1..24) disabled()
                        onClick { operate { TourOperationsApi.saveSlot(token, t, slot, time, capacity.toInt()) } }
                    }) { Text("Uložit nastavení ${slot.roundNo}. rundy") }
                }
                }
                if (!registrationMode) TourPresentationTable(entries,
                    current.placements.filter { it.drawId == validDraw?.id }, current.handicaps, current.players, current.jokerUses,
                    t.discipline, disabled || registrationClosed || isStarted,
                    onAttendance = { e, state -> operate { TourOperationsApi.entry(token, t, e, state, e.joker && state == "present") } },
                    onJoker = { e, joker -> operate { TourOperationsApi.entry(token, t, e, e.attendance, joker) } })
                if (waiting.isNotEmpty()) {
                    H4 { Text("Náhradníci ${slot.roundNo}. rundy") }
                    waiting.forEachIndexed { index, e -> Div(attrs = { classes("tour-waiting-row") }) {
                        Span { Text("${slot.capacity + index + 1}. ${e.playerName} · Náhradník") }
                        if (registrationMode) {
                            Button(attrs = {
                                if (disabled || registrationClosed || isStarted || active.size >= slot.capacity) disabled()
                                onClick { operate { TourOperationsApi.register(token, t, e.playerId, slot.id) } }
                            }) { Text("Zařadit náhradníka ${e.playerName}") }
                            Button(attrs = { if (disabled || registrationClosed) disabled(); onClick { operate { TourOperationsApi.entry(token, t, e, "cancelled", false) } } }) { Text("Odhlásit ${e.playerName}") }
                        }
                    } }
                }
                if (!isStarted) {
                    P { Text("Los zahrnuje všechny přihlášené hráče rundy. Neúčast zachová los; k osamocenému hráči se přesune jeden hráč z plné dráhy, pokud je k dispozici.") }
                    Div(attrs = { classes("tour-actions") }) {
                    Button(attrs = {
                        if (disabled || registrationClosed || active.isEmpty()) disabled()
                        onClick { if (slot.currentDrawId != null) { error = null; redraw = slot } else operate { TourOperationsApi.draw(token, t, slot) } }
                    }) { Text(if (slot.currentDrawId == null) "Vylosovat ${slot.roundNo}. rundu" else "Přelosovat ${slot.roundNo}. rundu") }
                    Button(attrs = {
                        if (disabled || registrationClosed || active.isEmpty()) disabled()
                        onClick { error = null; manualDraw = slot }
                    }) { Text(if (slot.currentDrawId == null) "Zadat los ${slot.roundNo}. rundy" else "Upravit los ${slot.roundNo}. rundy") }
                    }
                    if (validDraw == null && slot.currentDrawId != null) P(attrs = { classes("editor-error") }) { Text("Obsazení se změnilo. Uložený los již neplatí.") }
                    val ready = current.set != null && validDraw != null && active.isNotEmpty() && active.all { e ->
                        e.attendance == "present" && current.handicaps.any { it.playerId == e.playerId && it.groupCode != null }
                    }
                    if (!registrationMode) Button(attrs = { classes("primary"); if (disabled || registrationClosed || !ready) disabled(); onClick { error = null; start = slot } }) { Text("Zahájit ${slot.roundNo}. rundu") }
                    if (!registrationMode && !ready) P(attrs = { classes("muted") }) { Text("Pro start je potřeba platný los, dokončená prezentace a potvrzená sada s handicapem všech přítomných hráčů.") }
                }
                if (registrationMode) TourLaneGrid(slot, current.entries, validDraw, current.placements)
            }
        } }
        val waiting = current.entries.filter { it.slotId == null && it.waitlistSlotId == null && it.attendance != "cancelled" }.sortedBy { it.waitlistOrder }
        if (registrationMode && waiting.isNotEmpty()) Div(attrs = { classes("card") }) {
            H2 { Text("Náhradníci") }
            waiting.forEachIndexed { index, e -> Div(attrs = { classes("tour-waiting-row") }) {
                Span { Text("${index+1}. ${e.playerName}") }
                current.slots.filter { it.startedAt == null }.forEach { slot -> Button(attrs = {
                    if (disabled || registrationClosed) disabled(); onClick { move = e to slot.id }
                }) { Text("${e.playerName} → ${slot.roundNo}. runda") } }
                Button(attrs = { if (disabled) disabled(); onClick { operate { TourOperationsApi.entry(token, t, e, "cancelled", false) } } }) { Text("Odhlásit ${e.playerName}") }
            } }
        }
    }
    move?.let { (e, targetId) -> Div(attrs = { classes("card") }) {
        H2 { Text("Potvrdit přesun: ${e.playerName}") }
        P { Text("Cíl: " + (current.slots.firstOrNull { it.id == targetId }?.let { "${it.roundNo}. runda" } ?: "Náhradníci") + ". Los dotčených rund se zneplatní.") }
        Button(attrs = { if (disabled) disabled(); onClick { operate { TourOperationsApi.register(token, t, e.playerId, targetId.ifBlank { null }); move = null } } }) { Text("Potvrdit přesun") }
        Button(attrs = { if (busy) disabled(); onClick { move = null } }) { Text("Zrušit přesun") }
    } }
    manualDraw?.let { slot ->
        TourManualDrawDialog(slot, current.entries.filter { it.slotId == slot.id && it.occupiesLane() },
            current.placements.filter { it.drawId == slot.currentDrawId }, busy, !disabled && !registrationClosed, error,
            onClose = { manualDraw = null; error = null },
            onSave = { rows -> operate { TourOperationsApi.manualDraw(token, t, slot, rows); manualDraw = null } })
    }
    redraw?.let { slot ->
        TourRedrawDialog(slot, current.entries.count { it.slotId == slot.id && it.occupiesLane() }, busy,
            !disabled, error, onCancel = { redraw = null }, onConfirm = {
                operate { TourOperationsApi.draw(token, t, slot); redraw = null }
            })
    }
    start?.let { slot ->
        TourRoundStartDialog(slot, current.entries.count { it.slotId == slot.id && it.occupiesLane() }, busy,
            !disabled && current.set != null, error, onCancel = { start = null }, onConfirm = {
                operate { TourOperationsApi.start(token, t, slot, current.set!!); start = null }
            })
    }
}

@Composable
private fun TourLaneGrid(slot: TourSlotDto, entries: List<TourEntryDto>, draw: TourDrawDto?, placements: List<TourDrawEntryDto>) {
    H4 { Text("Rozlosování ${slot.roundNo}. rundy") }
    if (draw == null) P { Text("Platné rozlosování zatím není zveřejněno.") }
    else Div(attrs = { classes("tour-lanes") }) {
        (1..8).forEach { lane -> Div(attrs = { classes("tour-lane") }) {
            H4 { Text("Dráha $lane") }
            val positions = placements.filter { it.drawId == draw.id && it.lane == lane }.sortedBy { it.position }
            if (positions.isEmpty()) P { Text("Volná") }
            positions.forEach { pos ->
                val entry = entries.firstOrNull { it.id == pos.entryId }
                P {
                    Span(attrs = { if (entry?.attendance == "absent") classes("tour-absent-player") }) { Text("${pos.position}. ${entry?.playerName ?: "Hráč"}") }
                    if (entry?.attendance == "absent") Text(" · Nepřišel")
                    pos.movedFromLane?.let { Text(" · Přesun z dráhy $it") }
                }
            }
        } }
    }
}

@Composable
fun TourPublicRegistration(slug: String) {
    var data by remember(slug) { mutableStateOf<TourRegistrationData?>(null) }
    var error by remember(slug) { mutableStateOf<String?>(null) }
    var loading by remember(slug) { mutableStateOf(true) }
    var retry by remember { mutableStateOf(0) }
    LaunchedEffect(slug, retry) {
        loading = true; error = null; data = null
        try {
            val tournament = TourOperationsApi.tournamentBySlug(slug)
            if (tournament != null) data = loadTourRegistration(null, tournament.id)
        } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    Div(attrs = { classes("card") }) {
        H2 { Text(data?.tournament?.title ?: "Obsazovačka Tour") }
        data?.let { P { Text(tourLabel(it.tournament.status, TOUR_TOURNAMENT_STATES)) } }
        data?.entries?.filter { it.slotId == null && it.waitlistSlotId == null && it.attendance != "cancelled" }?.takeIf { it.isNotEmpty() }?.let { waiting ->
            P(attrs = { classes("muted") }) { Text("Náhradníci bez určené rundy: ${waiting.sortedBy { it.waitlistOrder }.joinToString { it.playerName }}") }
        }
        if (loading) P { Text("Načítám…") }
        TourError(error)
        if (!loading && error == null && data == null) P { Text("Turnaj nebyl nalezen nebo zatím není zveřejněn.") }
        if (error != null) Button(attrs = { onClick { retry++ } }) { Text("Načíst znovu") }
    }
    data?.let { current ->
        current.slots.forEach { slot -> Div(attrs = { classes("card") }) {
            val entries = current.entries.filter { it.slotId == slot.id && it.attendance != "cancelled" }
            val waiting = current.entries.filter { it.waitlistSlotId == slot.id && it.attendance != "cancelled" }.sortedBy { it.waitlistOrder }
            H2 { Text("${slot.roundNo}. runda · ${slot.startsAt.take(5)} · ${entries.count { it.occupiesLane() }}/${slot.capacity}") }
            if (slot.startedAt != null) P { Text("Runda zahájena") }
            Div(attrs = { classes("table-scroll") }) { Table(attrs = { classes("public-table"); attr("aria-label", "Obsazení ${slot.roundNo}. rundy") }) {
                Thead { Tr { listOf("Pořadí", "Hráč", "Účast").forEach { Th { Text(it) } } } }
                Tbody {
                    entries.forEachIndexed { index, e -> Tr {
                        Td { Text((index + 1).toString()) }
                        Td(attrs = { classes("tour-player-name"); if (e.attendance == "absent") classes("tour-absent-player") }) { Text(e.playerName) }
                        Td { Text(tourLabel(e.attendance, ATTENDANCE_LABELS)) }
                    } }
                    waiting.forEachIndexed { index, e -> Tr(attrs = { classes("tour-waitlist-entry"); if (index == 0) classes("tour-waitlist-start") }) {
                        Td { Text((slot.capacity + index + 1).toString()) }
                        Td(attrs = { classes("tour-player-name") }) { Text(e.playerName) }
                        Td { Text("Náhradník") }
                    } }
                }
            } }
            current.draws.firstOrNull { it.id == slot.currentDrawId }?.let { draw ->
                TourLaneGrid(slot, current.entries, draw, current.placements)
            }
        } }
    }
}
